import { createHash } from "node:crypto";
import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import { PLACES_AUTOCOMPLETE_MIN_CHARS } from "~/lib/places-autocomplete-constants";
import { normalizeMapsFreeText } from "~/lib/utils/normalize-maps-input";
import {
  mapsCallSourceSchema,
  type MapsCallSource,
} from "~/lib/maps/attribution";
import { guardedGoogleFetch } from "~/lib/server-functions/maps-outbound-call";
import {
  hashMapsRequestKey,
  hashSessionScope,
  logMapsUsage,
} from "~/lib/server-functions/maps-usage-log";
import {
  readPlacesAutocompleteCacheFromDb,
  writePlacesAutocompleteCacheToDb,
  type CachedPlacesAutocomplete,
  type PlacePrediction,
} from "~/lib/server-functions/places-autocomplete-cache-db";
import { guardMapsRequest } from "./maps-request-guard";
import {
  isValidPlacesSessionToken,
  recordPlacesAutocompleteSession,
} from "./maps-places-session";

/** Short stable id for correlating console lines (no raw query / PII). */
function hashForLog(value: string) {
  return createHash("sha256").update(value).digest("hex").slice(0, 12);
}

export type { PlacePrediction };

export const placesAutocompleteProfileSchema = z.enum(["regions", "address"]);
export type PlacesAutocompleteProfile = z.infer<
  typeof placesAutocompleteProfileSchema
>;

export type PlacesAutocompleteResponse = {
  predictions: PlacePrediction[];
  /** True when served from server memory or durable zero-cache (Google was not called). */
  servedFromCache: boolean;
  /** True when the shared outbound budget rejected the request (Google was not called). */
  blockedByBudget: boolean;
  /** True only when the server recorded this live session for terminal Details. */
  sessionEligible: boolean;
};

type CacheEntry = {
  entry: CachedPlacesAutocomplete;
  /** Absolute L1 expiry; when warmed from DB, capped by the durable row. */
  expiresAtMs: number;
};

// In-memory L1: full predictions (short TTL). Durable L2: zero-results only.
const autocompleteCache = new Map<string, CacheEntry>();
const AUTOCOMPLETE_CACHE_TTL_MS = 15 * 60 * 1000;
const AUTOCOMPLETE_NEGATIVE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const AUTOCOMPLETE_CACHE_MAX_ENTRIES = 750;
const DB_AUTOCOMPLETE_NEGATIVE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function evictAutocompleteCacheToMaxEntries(): void {
  while (autocompleteCache.size > AUTOCOMPLETE_CACHE_MAX_ENTRIES) {
    const oldestKey = autocompleteCache.keys().next().value as
      | string
      | undefined;
    if (!oldestKey) return;
    autocompleteCache.delete(oldestKey);
  }
}

function defaultL1ExpiresAtMs(
  entry: CachedPlacesAutocomplete,
  now: number,
): number {
  const ttlMs =
    entry.kind === "zero"
      ? AUTOCOMPLETE_NEGATIVE_CACHE_TTL_MS
      : AUTOCOMPLETE_CACHE_TTL_MS;
  return now + ttlMs;
}

/**
 * Warm / set L1. When `dbExpiresAt` is provided, L1 must not outlive the durable row
 * (including negative entries).
 */
function rememberInMemory(
  cacheKey: string,
  entry: CachedPlacesAutocomplete,
  dbExpiresAt?: Date,
): void {
  const now = Date.now();
  const defaultExpiresAtMs = defaultL1ExpiresAtMs(entry, now);
  const dbExpiresAtMs = dbExpiresAt?.getTime();
  const expiresAtMs =
    dbExpiresAtMs != null && Number.isFinite(dbExpiresAtMs)
      ? Math.min(defaultExpiresAtMs, dbExpiresAtMs)
      : defaultExpiresAtMs;

  if (expiresAtMs <= now) {
    autocompleteCache.delete(cacheKey);
    return;
  }

  autocompleteCache.set(cacheKey, { entry, expiresAtMs });
  evictAutocompleteCacheToMaxEntries();
}

function predictionsFromCached(
  entry: CachedPlacesAutocomplete,
): PlacePrediction[] {
  return entry.kind === "ok" ? entry.value : [];
}

export function parsePlacesAutocompleteSuggestions(
  data: unknown,
): PlacePrediction[] {
  const suggestions =
    data && typeof data === "object" && "suggestions" in data
      ? (data as { suggestions?: unknown }).suggestions
      : undefined;
  if (!Array.isArray(suggestions)) return [];
  return suggestions.flatMap((suggestion) => {
    const prediction = (suggestion as any)?.placePrediction;
    if (
      typeof prediction?.placeId !== "string" ||
      typeof prediction?.text?.text !== "string"
    ) {
      return [];
    }
    return [
      {
        placeId: prediction.placeId,
        description: prediction.text.text,
        mainText:
          prediction.structuredFormat?.mainText?.text ?? prediction.text.text,
        secondaryText: prediction.structuredFormat?.secondaryText?.text ?? "",
      },
    ];
  });
}

const EMPTY_CACHED: PlacesAutocompleteResponse = {
  predictions: [],
  servedFromCache: true,
  blockedByBudget: false,
  sessionEligible: false,
};

export const getPlacesAutocomplete = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      query: z.string().trim().min(1).max(120),
      sessionToken: z.string().trim().max(128).optional(),
      profile: placesAutocompleteProfileSchema.optional(),
      /** Callsite for cost attribution — a finite enum, never free text. */
      source: mapsCallSourceSchema,
    }),
  )
  .handler(async (ctx): Promise<PlacesAutocompleteResponse> => {
    const apiKey = process.env.GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      console.error("[places autocomplete] FAIL missing GOOGLE_MAPS_API_KEY");
      throw new Error(
        "Google Maps API key not configured (GOOGLE_MAPS_API_KEY)",
      );
    }

    const source: MapsCallSource = ctx.data.source;
    const query = ctx.data.query.trim();
    if (!query) {
      console.log("[places autocomplete] FAIL empty query after trim");
      return EMPTY_CACHED;
    }
    if (query.length < PLACES_AUTOCOMPLETE_MIN_CHARS) {
      console.log("[places autocomplete] FAIL query too short", {
        inputLength: query.length,
        minChars: PLACES_AUTOCOMPLETE_MIN_CHARS,
      });
      return EMPTY_CACHED;
    }

    const profile: PlacesAutocompleteProfile =
      ctx.data.profile ?? (source === "location_form" ? "address" : "regions");
    const normalizedQuery = normalizeMapsFreeText(query);
    const cacheKey = `${profile}|${normalizedQuery}`;
    // v3: Places API New and request profile are part of the durable key.
    const requestKeyHash = hashMapsRequestKey(
      `${normalizedQuery}|places_autocomplete|v3|new|${profile}|de`,
    );
    const autocompleteId = hashForLog(cacheKey);
    const log = (step: string, extra?: Record<string, unknown>) => {
      console.log(`[places autocomplete] ${step}`, {
        autocompleteId,
        inputLength: query.length,
        profile,
        source,
        memoryCacheSize: autocompleteCache.size,
        ...extra,
      });
    };

    log("1 start");

    const rawSessionToken = ctx.data.sessionToken?.trim() || undefined;
    const sessionToken =
      rawSessionToken && isValidPlacesSessionToken(rawSessionToken)
        ? rawSessionToken
        : undefined;
    const hadSessionToken = Boolean(sessionToken);

    // --- L1 memory ---
    const mem = autocompleteCache.get(cacheKey);
    if (mem) {
      if (Date.now() >= mem.expiresAtMs) {
        autocompleteCache.delete(cacheKey);
        log("2 memory cache expired → continue");
      } else {
        // LRU: refresh recency on access (keep the same absolute expiry).
        autocompleteCache.delete(cacheKey);
        autocompleteCache.set(cacheKey, mem);
        const predictions = predictionsFromCached(mem.entry);
        void logMapsUsage({
          api: "places_autocomplete",
          source,
          stage: "cache_hit",
          cacheLayer: "memory",
          outcome:
            mem.entry.kind === "zero"
              ? "cache_hit_memory_zero_results"
              : "cache_hit_memory",
          durationMs: 0,
          requestKeyHash,
          hadSessionToken,
        });
        if (mem.entry.kind === "zero" || predictions.length === 0) {
          log("2 FAIL memory cache hit (zero/empty)", {
            kind: mem.entry.kind,
            predictionCount: predictions.length,
          });
        } else {
          log("2 OK memory cache hit", {
            kind: mem.entry.kind,
            predictionCount: predictions.length,
          });
        }
        return {
          predictions,
          servedFromCache: true,
          blockedByBudget: false,
          sessionEligible: false,
        };
      }
    } else {
      log("2 memory cache miss");
    }

    // --- L2 DB (negative only) ---
    const fromDb = await readPlacesAutocompleteCacheFromDb(requestKeyHash);
    if (fromDb) {
      rememberInMemory(cacheKey, { kind: "zero" }, fromDb.expiresAt);
      void logMapsUsage({
        api: "places_autocomplete",
        source,
        stage: "cache_hit",
        cacheLayer: "database",
        outcome: "cache_hit_db_zero_results",
        durationMs: 0,
        requestKeyHash,
        hadSessionToken,
      });
      log("3 FAIL database negative cache hit", {
        expiresAt:
          fromDb.expiresAt instanceof Date
            ? fromDb.expiresAt.toISOString()
            : String(fromDb.expiresAt),
      });
      return {
        predictions: [],
        servedFromCache: true,
        blockedByBudget: false,
        sessionEligible: false,
      };
    }
    log("3 database cache miss");

    // --- Same-origin + per-client rate limit ---
    const requestGuard = await guardMapsRequest(
      "places_autocomplete",
      source,
      { requireOriginInProduction: true },
    );
    if (!requestGuard.allowed) {
      log("4 FAIL blocked by request guard", { reason: requestGuard.reason });
      return {
        predictions: [],
        servedFromCache: false,
        blockedByBudget: true,
        sessionEligible: false,
      };
    }
    log("4 request guard allowed");

    void logMapsUsage({
      api: "places_autocomplete",
      source,
      stage: "cache_miss",
      cacheLayer: "none",
      outcome: "cache_miss",
      durationMs: 0,
      requestKeyHash,
      hadSessionToken,
    });

    const url = "https://places.googleapis.com/v1/places:autocomplete";
    const requestBody = {
      input: query,
      languageCode: "de",
      ...(sessionToken ? { sessionToken } : {}),
      ...(profile === "regions" ? { includedPrimaryTypes: ["(regions)"] } : {}),
    };

    const timeoutMsRaw = Number(process.env.GOOGLE_MAPS_FETCH_TIMEOUT_MS);
    const timeoutMs =
      Number.isFinite(timeoutMsRaw) &&
      timeoutMsRaw >= 1000 &&
      timeoutMsRaw <= 60_000
        ? timeoutMsRaw
        : 12_000;

    log("5 calling guardedGoogleFetch", { hadSessionToken, timeoutMs });

    const guarded = await guardedGoogleFetch({
      api: "places_autocomplete",
      source,
      requestKeyHash,
      // Requests carrying different billing sessions must not coalesce.
      sessionScope: sessionToken ? hashSessionScope(sessionToken) : null,
      hadSessionToken,
      fetchFromGoogle: async (): Promise<{
        predictions: PlacePrediction[];
        sessionEligible: boolean;
      }> => {
        const started = Date.now();
        let usageLogged = false;
        log("6 fetching Google Places Autocomplete");

        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
          let response: Response;
          let data: any;
          try {
            response = await fetch(url, {
              method: "POST",
              signal: controller.signal,
              headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": apiKey,
                "X-Goog-FieldMask":
                  "suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.mainText.text,suggestions.placePrediction.structuredFormat.secondaryText.text",
              },
              body: JSON.stringify(requestBody),
            });
            data = await response.json();
          } finally {
            clearTimeout(timeoutId);
          }
          const durationMs = Date.now() - started;

          const placePredictions = parsePlacesAutocompleteSuggestions(data);
          const sessionEligible =
            response.ok && sessionToken
              ? await recordPlacesAutocompleteSession(sessionToken, source)
              : false;

          if (response.ok && placePredictions.length === 0) {
            usageLogged = true;
            await logMapsUsage({
              api: "places_autocomplete",
              source,
              stage: "google_result",
              outcome: "zero_results",
              googleStatus: "ZERO_RESULTS",
              httpStatus: response.status,
              durationMs,
              requestKeyHash,
              hadSessionToken,
            });
            const expiresAt = new Date(
              Date.now() + DB_AUTOCOMPLETE_NEGATIVE_CACHE_TTL_MS,
            );
            rememberInMemory(cacheKey, { kind: "zero" }, expiresAt);
            await writePlacesAutocompleteCacheToDb(
              requestKeyHash,
              { kind: "zero" },
              expiresAt,
            );
            log("6 FAIL Google zero results (negatively cached)", {
              httpStatus: response.status,
              durationMs,
            });
            return { predictions: [], sessionEligible };
          }

          if (!response.ok) {
            const googleStatus =
              typeof data.error?.status === "string"
                ? data.error.status
                : `HTTP_${response.status}`;
            usageLogged = true;
            await logMapsUsage({
              api: "places_autocomplete",
              source,
              stage: "google_result",
              outcome: "error",
              googleStatus,
              httpStatus: response.status,
              durationMs,
              requestKeyHash,
              hadSessionToken,
            });
            log("6 FAIL Google error response", {
              httpStatus: response.status,
              googleStatus,
              durationMs,
              errorMessage:
                typeof data.error?.message === "string"
                  ? data.error.message.slice(0, 200)
                  : undefined,
            });
            // Transient Google failures are never cached.
            const message =
              data.error?.message ||
              `Google Places Autocomplete API error: HTTP ${response.status}`;
            throw new Error(message);
          }

          if (placePredictions.length > 0) {
            usageLogged = true;
            await logMapsUsage({
              api: "places_autocomplete",
              source,
              stage: "google_result",
              outcome: "success",
              googleStatus: "OK",
              httpStatus: response.status,
              durationMs,
              requestKeyHash,
              hadSessionToken,
            });
            const result = placePredictions;
            // Positive hits: L1 only — durable cache cannot rebuild UI labels.
            rememberInMemory(cacheKey, { kind: "ok", value: result });
            log("6 OK Google success", {
              predictionCount: result.length,
              durationMs,
            });
            return { predictions: result, sessionEligible };
          }

          log("6 FAIL Google OK but no parseable predictions", {
            httpStatus: response.status,
            durationMs,
          });
          return { predictions: [], sessionEligible };
        } catch (err) {
          if (!usageLogged) {
            await logMapsUsage({
              api: "places_autocomplete",
              source,
              stage: "error",
              outcome: "error",
              googleStatus: null,
              durationMs: Date.now() - started,
              requestKeyHash,
              hadSessionToken,
            });
          }
          const errMeta =
            err instanceof Error
              ? { name: err.name, message: err.message.slice(0, 200) }
              : { name: "UnknownError" };
          console.error("[places autocomplete] 6 FAIL fetch threw", {
            autocompleteId,
            inputLength: query.length,
            profile,
            source,
            ...errMeta,
          });
          throw err;
        }
      },
    });

    if (guarded.status === "blocked") {
      // Degrade quietly: the input keeps working, it just shows no suggestions.
      log("5 FAIL blocked by outbound budget", {
        blockedBy: guarded.blockedBy,
        deduplicated: guarded.deduplicated,
      });
      return {
        predictions: [],
        servedFromCache: false,
        blockedByBudget: true,
        sessionEligible: false,
      };
    }

    // Coalesced requests share the leader's session scope, so Google did see
    // this Autocomplete session even when this call did not fetch itself.
    log("7 done", {
      predictionCount: guarded.value.predictions.length,
      sessionEligible: guarded.value.sessionEligible,
      deduplicated: guarded.deduplicated,
    });
    return {
      predictions: guarded.value.predictions,
      servedFromCache: false,
      blockedByBudget: false,
      sessionEligible: guarded.value.sessionEligible,
    };
  });
